/**
 * Normalizes decimal input by converting dots to commas
 * @param value - Input string from user
 * @returns Normalized string with comma as decimal separator
 * @example normalizeDecimalInput('0.5') => '0,5'
 * @example normalizeDecimalInput('123.45') => '123,45'
 * @example normalizeDecimalInput('100') => '100'
 */
export function normalizeDecimalInput(value: string | number): string {
  if (!value && value !== 0) return '';
  
  // Convert number to string
  const stringValue = typeof value === 'number' ? (isNaN(value) ? '' : value.toString()) : value;
  
  // Replace dot with comma
  return stringValue.replace(/\./g, ',');
}

/**
 * Parses a comma-separated decimal string to a float number
 * @param value - String value with comma as decimal separator
 * @returns Parsed float number or NaN if invalid
 * @example parseDecimalValue('0,5') => 0.5
 * @example parseDecimalValue('123,45') => 123.45
 * @example parseDecimalValue('100') => 100
 */
export function parseDecimalValue(value: string): number {
  if (!value) return NaN;
  
  // Replace comma with dot for JavaScript parsing
  const normalized = value.replace(/,/g, '.');
  return parseFloat(normalized);
}

/**
 * Formats a number to display with comma as decimal separator
 * @param value - Number to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string with comma separator
 * @example formatDecimalDisplay(0.5) => '0,5'
 * @example formatDecimalDisplay(123.456, 2) => '123,46'
 */
export function formatDecimalDisplay(value: number, decimals: number = 2): string {
  if (isNaN(value)) return '';
  
  return value.toFixed(decimals).replace(/\./g, ',');
}

/**
 * Validates if a string is a valid decimal number (with comma or dot)
 * @param value - String to validate
 * @returns True if valid decimal number
 * @example isValidDecimalInput('0,5') => true
 * @example isValidDecimalInput('0.5') => true
 * @example isValidDecimalInput('abc') => false
 */
export function isValidDecimalInput(value: string): boolean {
  if (!value) return true; // Empty is valid (will be handled as 0)
  
  // Allow numbers with optional comma or dot as decimal separator
  // Also allow negative numbers
  const pattern = /^-?\d*[,.]?\d*$/;
  return pattern.test(value);
}

/**
 * Handles decimal input onChange event
 * Normalizes the input and returns the parsed value
 * @param inputValue - Raw input value from event
 * @param fallbackValue - Value to return if parsing fails (default: 0)
 * @returns Object with normalized string and parsed number
 */
export function handleDecimalInputChange(
  inputValue: string,
  fallbackValue: number = 0
): { displayValue: string; numericValue: number } {
  // Normalize dots to commas
  const normalized = normalizeDecimalInput(inputValue);
  
  // Validate input
  if (!isValidDecimalInput(normalized)) {
    return {
      displayValue: '',
      numericValue: fallbackValue,
    };
  }
  
  // Parse to number
  const parsed = parseDecimalValue(normalized);
  const numericValue = isNaN(parsed) ? fallbackValue : parsed;
  
  return {
    displayValue: normalized,
    numericValue,
  };
}